import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/Layout';
import { RevoltGlassButton } from '../../components/RevoltGlassButton';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigationManager } from '../../core';
import './AcceptInviteSection.css';

/**
 * AcceptInviteSectionView
 * - Always enforces re-auth: clears existing session on entry, then requires login
 * - Loads invite metadata
 * - Accept happens only after login as invited email
 */
export function AcceptInviteSectionView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigationManager = useNavigationManager();
  const { isAuthenticated, user, loading: authLoading, authChecked, checkAuthStatus } = useAuth();

  const inviteToken = searchParams.get('inviteToken');
  const appId = searchParams.get('appId');

  const [status, setStatus] = useState('checking'); // checking | need-login | mismatch | ready | accepting | success | error
  const [message, setMessage] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null); // {appId, email, role, expiresAt}
  const [authSynced, setAuthSynced] = useState(false);
  const requestedAuthRef = useRef(false);
  const mismatchHandledRef = useRef(false);

  // On mount, sync auth with server to avoid stale client state
  useEffect(() => {
    const sync = async () => {
      try {
        await checkAuthStatus?.();
      } catch (e) {
        // ignore
      } finally {
        setAuthSynced(true);
      }
    };
    sync();
  }, [checkAuthStatus]);

  // Load invite metadata
  useEffect(() => {
    const loadInfo = async () => {
      if (!inviteToken) {
        setStatus('error');
        setMessage('Invalid or missing invite token.');
        return;
      }
      if (inviteInfo) return;
      try {
        const res = await fetch('/api/app-memberships/invite-info', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token: inviteToken })
        });
        if (!res.ok) {
          setStatus('error');
          setMessage('This invitation is invalid or has expired.');
          return;
        }
        const data = await res.json();
        setInviteInfo(data);
      } catch (err) {
        setStatus('error');
        setMessage('Unable to load invitation. Please try again.');
      }
    };
    loadInfo();
  }, [inviteInfo, inviteToken]);

  // Enforce login as invite email
  useEffect(() => {
    if (!inviteInfo) return;
    if (!authChecked || authLoading || !authSynced) {
      setStatus('checking');
      setMessage('Checking authentication...');
      return;
    }

    const sessionEmail = (user?.email || '').toLowerCase();
    const inviteEmail = (inviteInfo?.email || '').toLowerCase();

    // Not logged in -> require auth
    if (!isAuthenticated) {
      const returnUrl = `/accept-invite?inviteToken=${inviteToken}${appId ? `&appId=${appId}` : ''}`;
      if (!requestedAuthRef.current) {
        requestedAuthRef.current = true;
        navigationManager.requireAuth(returnUrl, { inviteToken, appId });
      }
      setStatus('need-login');
      setMessage(`Please sign in as ${inviteEmail || 'the invited email'} to continue.`);
      return;
    }

    // Logged in but mismatch -> force logout and re-auth
    if (inviteEmail && sessionEmail && inviteEmail !== sessionEmail) {
      if (!mismatchHandledRef.current) {
        mismatchHandledRef.current = true;
        requestedAuthRef.current = false;
        (async () => {
          try {
            await fetch('/api/account/logout', { method: 'POST', credentials: 'include' });
          } catch (e) {
            /* ignore */
          } finally {
            navigationManager.requireAuth(
              `/accept-invite?inviteToken=${inviteToken}${appId ? `&appId=${appId}` : ''}`,
              { inviteToken, appId }
            );
            setStatus('need-login');
            setMessage(`Please sign in as ${inviteEmail} to continue.`);
          }
        })();
      }
      return;
    }

    // Ready to accept
    mismatchHandledRef.current = false;
    setStatus('ready');
    setMessage('');
  }, [inviteInfo, authChecked, authLoading, isAuthenticated, user, appId, inviteToken, navigationManager, authSynced]);

  const handleAcceptClick = async () => {
    if (!inviteToken || !inviteInfo) return;
    // Re-sync auth before attempting accept
    await checkAuthStatus?.();
    setStatus('accepting');
    setMessage('Accepting invitation...');
    try {
      const res = await fetch('/api/app-memberships/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token: inviteToken })
      });
      if (res.status === 401 || res.status === 403) {
        requestedAuthRef.current = false;
        const returnUrl = `/accept-invite?inviteToken=${inviteToken}${appId ? `&appId=${appId}` : ''}`;
        navigationManager.requireAuth(returnUrl, { inviteToken, appId });
        setStatus('need-login');
        setMessage(`Please sign in as ${inviteInfo?.email || 'the invited email'} to continue.`);
        return;
      }
      if (!res.ok) {
        throw new Error('Accept failed');
      }
      setStatus('success');
      setMessage('Invitation accepted. Redirecting...');
      const target = appId ? `/app-settings/${appId}` : '/app';
      setTimeout(() => {
        navigate(target, { replace: true });
      }, 800);
    } catch (err) {
      setStatus('error');
      setMessage('Unable to accept invite. Please try again or request a new invite.');
    }
  };

  const renderContent = () => {
    if (status === 'checking') {
      return (
        <div className="ai-card-body">
          <div className="loading-spinner" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
          <p className="ai-muted">Checking invitation...</p>
        </div>
      );
    }

    if (status === 'need-login' || status === 'mismatch') {
      return (
        <div className="ai-card-body">
          <div className="alert alert-warning">{message || 'Please sign in with the invited account to continue.'}</div>
          {inviteInfo?.email && (
            <p className="ai-muted">Invite for: {inviteInfo.email}</p>
          )}
        </div>
      );
    }

    if (status === 'ready') {
      return (
        <div className="ai-card-body">
          <div className="alert alert-info" style={{ marginBottom: '16px' }}>
            You are signed in as {user?.email}. This invite is for {inviteInfo?.email}.
          </div>
          <RevoltGlassButton
            variant="accent"
            size="md"
            onClick={handleAcceptClick}
            style={{ width: '100%' }}
          >
            Accept Invitation
          </RevoltGlassButton>
        </div>
      );
    }

    if (status === 'accepting') {
      return (
        <div className="ai-card-body">
          <div className="loading-spinner" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
          <p className="ai-muted">Accepting invitation...</p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="ai-card-body">
          <div className="alert alert-success">{message || 'Invitation accepted. Redirecting...'}</div>
        </div>
      );
    }

    return (
      <div className="ai-card-body">
        <div className="alert alert-error">{message || 'Unable to process this invitation.'}</div>
      </div>
    );
  };

  return (
    <MainLayout contentClassName="main-content--accept-invite">
      <div className="accept-invite-container">
        <div className="accept-invite-card">
          <h1 className="ai-title">Accept Invitation</h1>
          <p className="ai-subtitle">Join the app you were invited to</p>
          {renderContent()}
        </div>
      </div>
    </MainLayout>
  );
}

export default AcceptInviteSectionView;

